import Raphael, {
  RaphaelAttributes,
  RaphaelElement,
  RaphaelPaper,
  RaphaelSet,
} from 'raphael';
import { useEffect, useRef, useState } from 'react';

import { Calculator } from '@suntex.core/animation';
import { $Color, ColorInput } from '@suntex.core/color';

import { BaseChartData, ChartDimensions, SectorConfig } from './types';

const MAX_SCORE = 100;
const ROUNDING_FACTOR = 10;
const NO_SELECTION = -1;

// Geometry
const HALF_DIVIDER = 2;
const FULL_CIRCLE_DEGREES = 360;
const HALF_CIRCLE_DEGREES = 180;
const QUARTER_CIRCLE_DEGREES = 90;
const RAD = Math.PI / HALF_CIRCLE_DEGREES;

// Animation config
const ANIMATION_MS = 500;
const ANIMATION_FAST_MS = 250;
const INNER_CIRCLE_SIZE_RATIO = 0.55;
const SUB_INDEX_CENTER_RATIO = 0.8;
const SUB_INDEX_RADIUS_RATIO = 0.2;
const HOVER_SCALE = 1.1;

export interface UseRaphaelChartOptions<T extends BaseChartData> {
  dimensions: ChartDimensions;
  sectors: readonly SectorConfig<T>[];
  data?: T;
  expanded?: boolean;
  onSectorHover?: (index: number) => void;
  onSectorLeave?: () => void;
}

export interface RaphaelChartAPI {
  paperRef: React.RefObject<HTMLDivElement>;
  sectorIndex: number;
  setSectorIndex: (index: number) => void;
}

/**
 * Custom hook for managing Raphael-based charts
 */
export function useRaphaelChart<T extends BaseChartData>({
  dimensions,
  sectors,
  data = {} as T,
  expanded,
  onSectorHover,
  onSectorLeave,
}: UseRaphaelChartOptions<T>): RaphaelChartAPI {
  const [sectorIndex, setSectorIndex] = useState<number>(NO_SELECTION);
  const paperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!paperRef.current) return;

    // Initialize Raphael paper
    const paper = Raphael(
      paperRef.current,
      dimensions.width,
      dimensions.height,
    );
    const cx = paper.width / HALF_DIVIDER;
    const cy = paper.height / HALF_DIVIDER;

    // Clear any existing content
    paper.clear();

    // Render the chart
    renderChart(
      paper,
      cx,
      cy,
      dimensions.radius,
      sectors,
      data,
      {
        onSectorHover: (index) => {
          setSectorIndex(index);
          onSectorHover?.(index);
        },
        onSectorLeave: () => {
          setSectorIndex(NO_SELECTION);
          onSectorLeave?.();
        },
      },
      expanded,
    );

    // Cleanup function
    return () => {
      paper.clear();
      paper.remove();
    };
  }, [dimensions, sectors, data, expanded, onSectorHover, onSectorLeave]);

  return {
    paperRef,
    sectorIndex,
    setSectorIndex,
  };
}

/**
 * Renders a sector-based chart using Raphael
 */
function renderChart<T extends BaseChartData>(
  paper: RaphaelPaper,
  cx: number,
  cy: number,
  radius: number,
  sectors: readonly SectorConfig<T>[],
  data: T,
  callbacks: {
    onSectorHover: (index: number) => void;
    onSectorLeave: () => void;
  },
  expanded?: boolean,
) {
  let totalScore = 0;
  let currentAngle = calculateStartAngle(sectors);

  // Collect all sector sets and sub-indices for center circle hover effect
  const allSectorSets: RaphaelSet[] = [];
  const allSubIndices: RaphaelSet[] = [];

  // Draw sectors
  sectors.forEach((sectorConfig, index) => {
    const set = paper.set();

    // Calculate sector properties
    const { angleSpan, score, adjustedRadius } = calculateSectorProperties(
      sectorConfig,
      data,
      radius,
    );
    const startAngle = currentAngle - angleSpan;
    const endAngle = currentAngle;
    totalScore += score;

    const color = $Color.from(sectorConfig.color);

    // Draw outer and inner sectors
    const outerSector = createSector(
      paper,
      cx,
      cy,
      radius,
      startAngle,
      endAngle,
      {
        fill: $Color.toHexString(color),
        'fill-opacity': 0.25,
        stroke: '#FFF',
        'stroke-width': 2,
      },
    );

    const innerSector = createSector(
      paper,
      cx,
      cy,
      adjustedRadius,
      startAngle,
      endAngle,
      {
        fill: `${Calculator.mod((startAngle + endAngle) / HALF_DIVIDER, FULL_CIRCLE_DEGREES)}-${$Color.toHexString(
          $Color.getGradientColor(color, 'shadow'),
        )}-${$Color.toHexString(color)}-${$Color.toHexString(
          $Color.getGradientColor(color, 'highlight'),
        )}`,
        stroke: '#FFF',
        'stroke-width': 2,
      },
    );

    // Create sub-index for score display
    const subIndexCenter = radialToCartesian(
      radius * SUB_INDEX_CENTER_RATIO,
      (startAngle + endAngle) / HALF_DIVIDER,
      cx,
      cy,
    );

    const subIndex = createSubIndex(
      paper,
      subIndexCenter,
      radius * SUB_INDEX_RADIUS_RATIO,
      roundToDecimalPlaces(score),
    );

    // Create transparent sector for mouse events
    const eventSector = createSector(
      paper,
      cx,
      cy,
      radius,
      startAngle,
      endAngle,
      {
        fill: 'transparent',
        stroke: 'transparent',
        cursor: 'pointer',
      },
    );

    // Add elements to set
    set.push(outerSector, innerSector, eventSector);

    // Store sector set and sub-index for center circle hover effect
    allSectorSets.push(set);
    allSubIndices.push(subIndex);

    // Add hover animations
    createHoverAnimations(
      set,
      subIndex,
      cx,
      cy,
      () => callbacks.onSectorHover(index),
      () => callbacks.onSectorLeave(),
      expanded,
    );

    // Update for next iteration
    currentAngle = startAngle;
  });

  // Draw center circle
  const centerRadius = radius * INNER_CIRCLE_SIZE_RATIO;
  createCenterCircle(
    paper,
    cx,
    cy,
    centerRadius,
    roundToDecimalPlaces(totalScore),
  );

  // Add hover functionality to center circle that affects all sectors
  const centerCircle = paper.circle(cx, cy, centerRadius).attr({
    fill: 'transparent',
    stroke: 'transparent',
    cursor: 'pointer',
  });

  if (expanded) {
    allSectorSets.forEach((sectorSet) => {
      animateExpandSector(sectorSet, cx, cy);
    });
    allSubIndices.forEach(animateExpandSubIndex);
  } else {
    centerCircle
      .mouseover(() => {
        allSectorSets.forEach((sectorSet) =>
          animateExpandSector(sectorSet, cx, cy),
        );
        allSubIndices.forEach(animateExpandSubIndex);
      })
      .mouseout(() => {
        allSectorSets.forEach(animateContractSector);
        allSubIndices.forEach(animateContractSubIndex);
      });
  }
}

/**
 * Calculates the starting angle for the chart
 */
function calculateStartAngle<T extends BaseChartData>(
  sectors: readonly SectorConfig<T>[],
): number {
  const firstSectorRatio = (sectors[0]?.weight ?? 0) / MAX_SCORE;
  return QUARTER_CIRCLE_DEGREES + firstSectorRatio * HALF_CIRCLE_DEGREES;
}

/**
 * Calculates properties for a single sector
 */
function calculateSectorProperties<T extends BaseChartData>(
  sectorConfig: SectorConfig<T>,
  data: T,
  radius: number,
) {
  // Calculate angle span
  const angleSpan =
    ((sectorConfig.weight || 1) * FULL_CIRCLE_DEGREES) / MAX_SCORE;

  // Get score value
  const score = data[sectorConfig.scorePropertyName] || 0;

  // Calculate score percentage
  //  const maxValue = sectorConfig.weight || MAX_SCORE;
  const scorePct = score / sectorConfig.weight;

  // Calculate adjusted radius based on score
  const adjustedRadius =
    radius *
    (scorePct * (1 - INNER_CIRCLE_SIZE_RATIO) + INNER_CIRCLE_SIZE_RATIO);

  return {
    angleSpan,
    score,
    scorePct,
    adjustedRadius,
  };
}

/**
 * Converts radial coordinates to cartesian coordinates
 */
function radialToCartesian(
  r: number,
  angle: number,
  cx: number,
  cy: number,
): { x: number; y: number } {
  const centerX = cx || 0;
  const centerY = cy || 0;
  const x = centerX + r * Math.cos(-angle * RAD);
  const y = centerY + r * Math.sin(-angle * RAD);
  return { x, y };
}

/**
 * Creates a sector path in Raphael
 */
function createSector(
  paper: RaphaelPaper,
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
  params: Partial<RaphaelAttributes>,
): RaphaelElement {
  const coord1 = radialToCartesian(r, startAngle, cx, cy);
  const coord2 = radialToCartesian(r, endAngle, cx, cy);

  return paper
    .path([
      ['M', cx, cy],
      ['L', coord1.x, coord1.y],
      [
        'A',
        r,
        r,
        0,
        +(endAngle - startAngle > HALF_CIRCLE_DEGREES),
        0,
        coord2.x,
        coord2.y,
      ],
      ['z'],
    ])
    .attr(params);
}

/**
 * Rounds a number to a specified number of decimal places
 */
function roundToDecimalPlaces(
  value: number,
  decimalPlaces: number = 1,
): number {
  const factor = Math.pow(ROUNDING_FACTOR, decimalPlaces);
  return Math.round(value * factor) / factor;
}

/**
 * Creates hover animations for chart sectors
 */
function createHoverAnimations(
  set: RaphaelSet,
  subIndex: RaphaelSet,
  cx: number,
  cy: number,
  onMouseOver: () => void,
  onMouseOut: () => void,
  expanded?: boolean,
): void {
  set
    .mouseover(() => {
      if (!expanded) {
        animateExpandSector(set, cx, cy);
        animateExpandSubIndex(subIndex);
      }
      onMouseOver();
    })
    .mouseout(() => {
      if (!expanded) {
        animateContractSector(set);
        animateContractSubIndex(subIndex);
      }
      onMouseOut();
    });
}

/**
 * Creates a sub-index circle with score display
 */
function createSubIndex(
  paper: RaphaelPaper,
  center: { x: number; y: number },
  radius: number,
  score: number,
  fontSize: number = 16,
  circleColor: string = '#FFF',
  textColor: string = '#000',
): RaphaelSet {
  return paper
    .set(
      paper.circle(center.x, center.y, radius).attr({
        fill: circleColor,
        'fill-opacity': 1.0,
        stroke: 'none',
      }),
      paper.text(center.x, center.y, score.toString()).attr({
        'font-weight': 'bold',
        'font-size': fontSize,
        fill: textColor,
      }),
    )
    .attr({ opacity: 0 });
}

/**
 * Creates a center circle with gradient and text
 */
function createCenterCircle(
  paper: RaphaelPaper,
  cx: number,
  cy: number,
  radius: number,
  totalScore: number | string,
  color: ColorInput = '#FC0',
  stroke: string = '#fff',
  fontSize: number = 40,
): void {
  const c = $Color.from(color);
  const fill = `90-${$Color.toHexString(c)}-${$Color.toHexString(
    $Color.getGradientColor(c, 'shadow'),
  )}`;
  // Draw center circle
  paper.circle(cx, cy, radius).attr({
    fill,
    stroke,
    'stroke-width': 2,
  });

  // Draw center text
  paper.text(cx, cy, totalScore.toString()).attr({
    'font-size': fontSize,
    'font-weight': 'bold',
    fill: '#fff',
    cursor: 'default',
  });
}

function animateExpandSector(set: RaphaelSet, cx: number, cy: number) {
  set.stop().animate(
    {
      transform: `s${HOVER_SCALE} ${HOVER_SCALE} ${cx} ${cy}`,
    },
    ANIMATION_MS,
    'elastic',
  );
}

function animateExpandSubIndex(subIndex: RaphaelSet) {
  subIndex.stop().animate({ opacity: 1 }, ANIMATION_FAST_MS);
}

function animateContractSector(set: RaphaelSet) {
  set.stop().animate({ transform: '' }, ANIMATION_MS, 'elastic');
}

function animateContractSubIndex(subIndex: RaphaelSet) {
  subIndex.stop().animate({ opacity: 0 }, ANIMATION_FAST_MS);
}
